import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Input, Message } from 'semantic-ui-react';
import { useLocation } from 'react-router-dom';

interface AddToFavoritesModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (name: string) => void;
}

// Helper function to generate a default name from the HTML page title
const getDefaultNameFromPageTitle = (): string => {
  const pageTitle = document.title;
  const hasQueryString = window.location.search.length > 0;
  
  if (!pageTitle || pageTitle.trim() === '') {
    return ''; // No name - user needs to enter it
  }
  
  // Extract the main part of the title (before the last pipe separator if it exists)
  const parts = pageTitle.split('|');
  let mainTitle = parts.length > 1 ? parts.slice(0, -1).join('|').trim() : pageTitle.trim();
  
  // Replace common title separators with " - " for consistency
  // Only replace when they act as separators (surrounded by spaces or at boundaries)
  mainTitle = mainTitle
    .replace(/\s*\|\s*/g, ' - ')  // Replace " | " with " - "
    .trim();
  
  // If there's a query string, append " - Custom filter"
  if (hasQueryString) {
    return `${mainTitle} - Custom filter`;
  }
  
  return mainTitle;
};

export const AddToFavoritesModal: React.FC<AddToFavoritesModalProps> = ({
  open,
  onClose,
  onConfirm
}) => {
  const location = useLocation();
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      const defaultName = getDefaultNameFromPageTitle();
      setName(defaultName);
      setError('');
      setIsSubmitting(false);
    }
  }, [open]);

  const handleSubmit = async () => {
    const trimmedName = name.trim();
    
    if (!trimmedName) {
      setError('Please enter a name for this favorite');
      return;
    }
    
    if (trimmedName.length > 50) {
      setError('Name must be 50 characters or less');
      return;
    }

    setIsSubmitting(true);
    setError('');
    
    try {
      onConfirm(trimmedName);
    } catch (err) {
      setError('Failed to add favorite. Please try again.');
      setIsSubmitting(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isSubmitting) {
      handleSubmit();
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="tiny"
      className="add-favorites-modal"
    >
      <Modal.Header>
        Add to Favorites
      </Modal.Header>
      <Modal.Content>
        <Form>
          <Form.Field>
            <label htmlFor="favorite-name">
              Give this page a name
            </label>
            <Input
              id="favorite-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Enter a name for this favorite"
              maxLength={50}
              autoFocus
              disabled={isSubmitting}
            />
            <div style={{ 
              textAlign: 'right', 
              fontSize: '12px', 
              color: '#666', 
              marginTop: '4px' 
            }}>
              {name.length}/50
            </div>
          </Form.Field>
          
          {error && (
            <Message negative size="small">
              {error}
            </Message>
          )}
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button
          onClick={onClose}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          primary
          onClick={handleSubmit}
          loading={isSubmitting}
          disabled={!name.trim() || isSubmitting}
        >
          Add to Favorites
        </Button>
      </Modal.Actions>
    </Modal>
  );
};
